import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PrivateRoute } from './PrivateRoute';
import ProtectedRoute from './ProtectedRoute';
import FeatureProtectedRoute from './FeatureProtectedRoute';
import SessionPersistenceTest from './SessionPersistenceTest';
import ArchivedProperties from './ArchivedProperties';
import ArchivedTenants from './ArchivedTenants';
import AccountManagement from './auth/AccountManagement';
import { usePageTracking } from '../hooks/useAnalytics';

// Enhanced lazy loading with error handling
const createLazyComponent = (importFunc) => {
  return React.lazy(() => {
    return importFunc().then(module => {
      // Ensure we're getting a valid React component
      // React.memo() returns a special object, not a plain function, so we need to check for both
      if (module.default && (
        typeof module.default === 'function' || 
        (typeof module.default === 'object' && module.default.$$typeof)
      )) {
        return module;
      } else {
        console.error('Invalid component export:', module);
        // Return a fallback component if the export is invalid
        return {
          default: () => (
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-red-600 mb-2">Component Loading Error</h3>
                <p className="text-gray-600">The requested component could not be loaded properly.</p>
              </div>
            </div>
          )
        };
      }
    }).catch(error => {
      console.error('Failed to load component:', error);
      return {
        default: () => (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-red-600 mb-2">Loading Failed</h3>
              <p className="text-gray-600">Unable to load the requested component.</p>
              <p className="text-sm text-gray-500 mt-2">{error.message}</p>
            </div>
          </div>
        )
      };
    });
  });
};

// Lazy load components with enhanced error handling
const Dashboard = createLazyComponent(() => import('./Dashboard'));
const DashboardNew = createLazyComponent(() => import('./DashboardNew'));
const Properties = createLazyComponent(() => import('./Properties'));
const PropertyDetails = createLazyComponent(() => import('./PropertyDetails'));
const PropertyDetailsV2 = createLazyComponent(() => import('./PropertyDetailsV2'));
const AddProperty = createLazyComponent(() => import('./AddProperty'));
const EditProperty = createLazyComponent(() => import('./EditProperty'));
const Tenants = createLazyComponent(() => import('./Tenants'));
const AddTenant = createLazyComponent(() => import('./AddTenant'));
const TenantDetails = createLazyComponent(() => import('./TenantDetails'));
const EditTenant = createLazyComponent(() => import('./EditTenant'));
const Maintenance = createLazyComponent(() => import('./Maintenance'));
const MaintenanceDetails = createLazyComponent(() => import('./MaintenanceDetails'));
const Inspections = createLazyComponent(() => import('./Inspections'));
const Finance = createLazyComponent(() => import('./Finance'));
const Reports = createLazyComponent(() => import('./Reports'));
const Settings = createLazyComponent(() => import('./Settings'));
const Profile = createLazyComponent(() => import('./Profile'));
const Landlords = createLazyComponent(() => import('./Landlords'));
const LandlordDetails = createLazyComponent(() => import('./LandlordDetails'));
const AddLandlord = createLazyComponent(() => import('./AddLandlord'));
const Notifications = createLazyComponent(() => import('./Notifications'));
const Payments = createLazyComponent(() => import('./Payments'));
const Messages = createLazyComponent(() => import('./Messages'));
const Links = createLazyComponent(() => import('./Links'));
const Calendar = createLazyComponent(() => import('./Calendar'));
const CompanyDocuments = createLazyComponent(() => import('./CompanyDocuments'));
const EditPayment = createLazyComponent(() => import('./EditPayment'));
const PermissionSyncPage = createLazyComponent(() => import('../pages/PermissionSyncPage'));
const TestOCRPage = createLazyComponent(() => import('../pages/TestOCRPage'));
const Archive = createLazyComponent(() => import('./Archive'));
const Compliance = createLazyComponent(() => import('./Compliance'));
const Contractors = createLazyComponent(() => import('./Contractors'));
const ContractorMessages = createLazyComponent(() => import('./ContractorMessages'));
const Broadcast = createLazyComponent(() => import('./Broadcast'));
const Contacts = createLazyComponent(() => import('./Contacts'));


// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-secondary">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="mt-4 text-text-secondary">Loading...</span>
    </div>
  </div>
);

/**
 * AuthenticatedApp Component
 * 
 * This component handles:
 * - All authenticated user routes
 * - Main app layout integration (works with existing TopBar, Sidebar via PrivateRoute)
 * - Route protection and access control
 * - Protected route management
 * - Lazy loading of route components
 * 
 * The component maintains the existing structure where PrivateRoute provides
 * the TopBar and Sidebar layout, and individual routes are wrapped with
 * ProtectedRoute for page-level access control.
 */
const AuthenticatedApp = () => {
  // Track page views automatically
  usePageTracking();
  const location = useLocation();

  console.log('🔵 AuthenticatedApp rendering, location.pathname:', location.pathname, 'location.hash:', location.hash);
  console.log('🔵 All routes should be available, checking for /tenants and /contractors');

  return (
    <Suspense fallback={<LoadingFallback />}>
      <div className="min-h-screen w-full bg-background text-text-primary">
        <Routes>
          {/* Dashboard Routes */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard-new"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="dashboard">
                  <DashboardNew />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Property Routes */}
          <Route
            path="/properties"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <Properties />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/properties/add"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <AddProperty />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/properties/:id"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <PropertyDetailsV2 />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/properties/:id/legacy"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <PropertyDetails />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-property/:id"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <EditProperty />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/archived-properties"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <ArchivedProperties />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/properties/archived"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <ArchivedProperties />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Tenant Routes */}
          <Route
            path="/tenants"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="tenants">
                  <Tenants />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/tenants/add"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="tenants">
                  <AddTenant />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/tenants/:id"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="tenants">
                  <TenantDetails />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-tenant/:id"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="tenants">
                  <EditTenant />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/archived-tenants"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="tenants">
                  <ArchivedTenants />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/tenants/archived"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="tenants">
                  <ArchivedTenants />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Maintenance Routes */}
          <Route
            path="/maintenance"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="maintenance">
                  <Maintenance />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/maintenance/:id"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="maintenance">
                  <MaintenanceDetails />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Inspections Route */}
          <Route
            path="/inspections"
            element={
              <PrivateRoute>
                <FeatureProtectedRoute featureName="inspections" redirectTo="/dashboard">
                  <ProtectedRoute pageName="inspections">
                    <Inspections />
                  </ProtectedRoute>
                </FeatureProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Finance Routes */}
          <Route
            path="/finance"
            element={
              <PrivateRoute>
                <FeatureProtectedRoute featureName="finance_module" redirectTo="/dashboard">
                  <ProtectedRoute pageName="finance">
                    <Finance />
                  </ProtectedRoute>
                </FeatureProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute>
                <FeatureProtectedRoute featureName="finance_module" redirectTo="/dashboard">
                  <ProtectedRoute pageName="finance">
                    <Payments />
                  </ProtectedRoute>
                </FeatureProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-payment/:id"
            element={
              <PrivateRoute>
                <FeatureProtectedRoute featureName="finance_module" redirectTo="/dashboard">
                  <ProtectedRoute pageName="finance">
                    <EditPayment />
                  </ProtectedRoute>
                </FeatureProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Reports Route */}
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <FeatureProtectedRoute featureName="finance_module" redirectTo="/dashboard">
                  <ProtectedRoute pageName="reports">
                    <Reports />
                  </ProtectedRoute>
                </FeatureProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Contractors Route */}
          <Route
            path="/contractors"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="contractors">
                  <Contractors />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Archive Route - Admin Only */}
          <Route
            path="/archive"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="archive">
                  <Archive />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Test OCR Route - Administrator only */}
          <Route
            path="/test"
            element={
              <PrivateRoute>
                <TestOCRPage />
              </PrivateRoute>
            }
          />

          {/* Settings Route */}
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="settings">
                  <Settings />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Profile Route */}
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          {/* Landlord Routes */}
          <Route
            path="/landlords"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="landlords">
                  <Landlords />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/landlords/:id"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="landlords">
                  <LandlordDetails />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/add-landlord"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="landlords">
                  <AddLandlord />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Notification Routes */}
          <Route
            path="/notifications"
            element={
              <PrivateRoute>
                <Notifications />
              </PrivateRoute>
            }
          />

          {/* Compliance Route */}
          <Route
            path="/compliance"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <Compliance />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Messages Route */}
          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="messages">
                  <Messages />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Contractor messages (agents/owners only; viewContractors) */}
          <Route
            path="/contractor-messages"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="contractors">
                  <ContractorMessages />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Links Route */}
          <Route
            path="/links"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="links">
                  <Links />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Calendar Route */}
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="calendar">
                  <Calendar />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Contacts Route */}
          <Route
            path="/contacts"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="properties">
                  <Contacts />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Company Documents Route */}
          <Route
            path="/company-documents"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="companyDocuments">
                  <CompanyDocuments />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Broadcast Route */}
          <Route
            path="/broadcast"
            element={
              <PrivateRoute>
                <ProtectedRoute pageName="broadcast" requiredFeature="sendBroadcasts">
                  <Broadcast />
                </ProtectedRoute>
              </PrivateRoute>
            }
          />

          {/* Account Management Routes */}
          <Route 
            path="/account" 
            element={
              <PrivateRoute>
                <AccountManagement />
              </PrivateRoute>
            } 
          />
          {/* Backward compatibility route */}
          <Route 
            path="/account-management" 
            element={<Navigate to="/account" replace />} 
          />

          {/* Admin/Development Routes */}
          <Route path="/permission-sync" element={<PermissionSyncPage />} />
          
          {/* Session Persistence Test Route (Development Only) */}
          {process.env.NODE_ENV === 'development' && (
            <Route 
              path="/session-test" 
              element={
                <PrivateRoute>
                  <SessionPersistenceTest />
                </PrivateRoute>
              } 
            />
          )}
          
          {/* Catch-all redirect to dashboard */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Suspense>
  );
}

export default AuthenticatedApp;